<template>
  <div class="admin-dashboard">
    <header class="dashboard-header">
      <div class="header-brand">
        <img :src="logo" alt="Asador San Miguel" class="brand-logo" />
        <div>
          <p class="eyebrow">Asador San Miguel · Gestión interna</p>
          <h1>Panel de administración</h1>
          <p>Monitorea solicitudes, controla cupo por hora y envía confirmaciones sin salir de esta vista.</p>
        </div>
      </div>
      <div class="metric-grid">
        <a class="metric-card" :class="{ 'metric-card--warn': pendingReservations.length > 0 }" href="#pending-reservations-section">
          <p>Pendientes</p>
          <h3>{{ pendingReservations.length }}</h3>
        </a>
        <div class="metric-card">
          <p>Activas</p>
          <h3>{{ filteredReservations.length }}</h3>
        </div>
        <div class="metric-card">
          <p>Total futuro</p>
          <h3>{{ confirmedReservations.length }}</h3>
        </div>
        <button class="metric-card metric-card--danger" v-if="failedPaymentAlert" @click="openFailedPaymentsModal">
          <p>Pagos fallidos</p>
          <h3>{{ failedPaymentReservations.length }}</h3>
        </button>
        <button class="metric-card metric-card--danger" v-if="paymentIncidentAlert" @click="openPaymentIncidentsModal">
          <p>Incidentes de pago</p>
          <h3>{{ paymentIncidents.length }}</h3>
        </button>
        <a class="metric-card metric-card--refunded" v-if="refundedAlert" href="#refunded-section">
          <p>Reembolsados</p>
          <h3>{{ refundedReservations.length }}</h3>
        </a>
      </div>
    </header>

    <section class="seating-summary">
      <div class="summary-card">
        <div class="summary-card-header">
          <p>Reservaciones de {{ hourlySummaryLabel }}</p>
          <div class="summary-date-actions">
            <button class="btn ghost btn-sm" type="button" @click="setHourlySummaryDateToToday">Hoy</button>
            <button class="btn ghost btn-sm" type="button" @click="setHourlySummaryDateToTomorrow">Mañana</button>
          </div>
        </div>
        <input
          v-model="hourlySummaryDate"
          type="date"
          class="summary-date-input"
        />
        <h3>{{ reservationHourlyTotals.totalPeople }}</h3>
        <p>{{ reservationHourlySummary.length }} horas con movimiento</p>
        <div class="summary-breakdown">
          <span>Total reservado: {{ reservationHourlyTotals.totalPeople }}</span>
        </div>
      </div>
      <div class="summary-card summary-card--wide">
        <p>Reservaciones por hora</p>
        <div v-if="reservationHourlySummary.length" class="hourly-flow-list">
          <div
            v-for="slot in reservationHourlySummary"
            :key="`hourly-flow-${slot.hourKey}`"
            class="hourly-flow-row"
          >
            <strong>{{ formatTime(slot.bucketTime) }}</strong>
            <span>Total: {{ slot.totalPeople }}</span>
          </div>
        </div>
        <p v-else class="note">No hay reservaciones registradas para esa fecha.</p>
      </div>
      <div class="summary-card">
        <div class="summary-card-header">
          <p>Walk-ins de {{ hourlySummaryLabel }}</p>
        </div>
        <h3>{{ walkInHourlyTotals.totalPeople }}</h3>
        <p>{{ walkInHourlySummary.length }} horas con movimiento</p>
        <div class="summary-breakdown">
          <span>En espera: {{ queuedWalkIns.length }}</span>
          <span>Atendidos: {{ attendedWalkIns.length }}</span>
        </div>
      </div>
      <div class="summary-card summary-card--wide">
        <p>Walk-ins por hora</p>
        <div v-if="walkInHourlySummary.length" class="hourly-flow-list">
          <div
            v-for="slot in walkInHourlySummary"
            :key="`walkin-hourly-flow-${slot.hourKey}`"
            class="hourly-flow-row"
          >
            <strong>{{ formatTime(slot.bucketTime) }}</strong>
            <span>Total: {{ slot.totalPeople }}</span>
          </div>
        </div>
        <p v-else class="note">No hay walk-ins registrados para esa fecha.</p>
      </div>
    </section>

    <section class="action-bar">
      <div class="action-group">
        <button class="btn primary" @click="openReservationModal">Añadir reservación</button>
        <button class="btn" @click="startQRCodeScanner">Escanear QR</button>
        <button class="btn" @click="openScannedReservationsModal">Reservaciones escaneadas</button>
        <button class="btn" @click="openScheduleModal">Gestionar horarios</button>
        <button class="btn" @click="openPaymentSettingsModal">Configurar cobro Clover</button>
        <button
          class="btn"
          @click="backfillHistoricalVoids"
          :disabled="backfillingHistoricalVoids"
        >
          <span v-if="backfillingHistoricalVoids">Buscando auto-voids...</span>
          <span v-else>Detectar auto-voids históricos</span>
        </button>
        <button class="btn" @click="openWalkInModal">Registrar walk-in</button>
        <button class="btn" @click="openCalendarModal">Ver calendario</button>
        <button
          class="btn danger fill"
          @click="cleanOldReservations"
          :disabled="cleaningOldReservations"
        >
          <span v-if="cleaningOldReservations">Limpiando...</span>
          <span v-else>Limpiar reservas vencidas</span>
        </button>
      </div>
      <a class="alert-chip alert-chip--pending" v-if="pendingReservations.length" href="#pending-reservations-section">
        <span class="pulse-dot pulse-dot--pending"></span>
        {{ pendingReservations.length === 1 ? '1 reservación pendiente de confirmación' : `${pendingReservations.length} reservaciones pendientes de confirmación` }}
      </a>
      <div class="alert-chip" v-if="newReservationAlert" @click="goToNewReservation">
        <span class="pulse-dot"></span>
        ¡Nueva solicitud de reservación!
      </div>
      <button class="alert-chip alert-chip--danger" v-if="failedPaymentAlert" @click="openFailedPaymentsModal">
        <span class="pulse-dot pulse-dot--danger"></span>
        {{ failedPaymentReservations.length === 1 ? '1 pago fallido' : `${failedPaymentReservations.length} pagos fallidos` }} — requiere atención
      </button>
      <button class="alert-chip alert-chip--danger" v-if="paymentIncidentAlert" @click="openPaymentIncidentsModal">
        <span class="pulse-dot pulse-dot--danger"></span>
        {{ paymentIncidents.length === 1 ? '1 incidente de pago abierto' : `${paymentIncidents.length} incidentes de pago abiertos` }}
      </button>
      <a class="alert-chip alert-chip--refunded" v-if="refundedAlert" href="#refunded-section">
        <span class="pulse-dot pulse-dot--refunded"></span>
        {{ refundedReservations.length === 1 ? '1 pago reembolsado por Clover' : `${refundedReservations.length} pagos reembolsados por Clover` }}
      </a>
      <p class="cleanup-note">
        Conservamos cada reservación durante 24 horas después de su fecha.
        <span v-if="cleanedReservationsCount"> Última limpieza: {{ cleanedReservationsCount }} reservaciones archivadas.</span>
      </p>
      <p class="cleanup-note">
        Cobro Clover: <strong>{{ paymentSettingsSummary }}</strong>.
      </p>
    </section>

    <section class="search-panel">
      <label>Buscar por email o teléfono</label>
      <div class="search-input">
        <span class="search-icon">🔍</span>
        <input
          v-model="searchQuery"
          placeholder="Ej. maria@email.com o 5551234567"
          @input="filterReservations"
        />
      </div>
    </section>



    <section v-if="refundedAlert" id="refunded-section" class="refunded-section">
      <header class="refunded-section__header">
        <div>
          <h2>💸 Pagos reembolsados / anulados por Clover</h2>
          <p>Clover procesó un reembolso o anuló el cobro en estas reservaciones. El cliente fue notificado por email.</p>
        </div>
      </header>
      <ul class="refunded-list">
        <li v-for="r in refundedReservations" :key="r.id" class="refunded-card">
          <div class="refunded-card__main">
            <div class="refunded-card__identity">
              <strong>{{ r.name }}</strong>
              <span>{{ r.email }}</span>
              <span v-if="r.phone">{{ r.phone }}</span>
            </div>
            <div class="refunded-card__details">
              <span class="refunded-detail">
                <span class="refunded-detail__label">Fecha</span>
                <strong>{{ formatAdminDate(r.date) }}</strong>
              </span>
              <span class="refunded-detail">
                <span class="refunded-detail__label">Hora</span>
                <strong>{{ formatTime(r.time) }}</strong>
              </span>
              <span class="refunded-detail">
                <span class="refunded-detail__label">Personas</span>
                <strong>{{ r.people }}</strong>
              </span>
              <span class="refunded-detail">
                <span class="refunded-detail__label">Monto reembolsado</span>
                <strong>{{ r.refundAmount ? `$${(r.refundAmount / 100).toFixed(2)}` : formatReservationPaymentAmount(r) }}</strong>
              </span>
            </div>
            <div class="refunded-card__reason" v-if="r.refundReason">
              <span class="refunded-card__reason-label">Motivo Clover</span>
              <p>{{ r.refundReason }}</p>
            </div>
            <p class="refunded-card__time" v-if="r.refundedAt">
              Reembolsado: {{ new Date(r.refundedAt).toLocaleString('es-PR', { timeZone: 'America/Puerto_Rico' }) }}
            </p>
          </div>
          <div class="refunded-card__badge">
            <span class="badge badge--refunded">Cancelado · Reembolsado</span>
          </div>
        </li>
      </ul>
    </section>

    <section v-if="pendingReservations.length" id="pending-reservations-section" class="pending-section pending-section--highlight">
      <header>
        <h2>
          <span class="pulse-dot pulse-dot--pending" style="display:inline-block;margin-right:8px;vertical-align:middle;"></span>
          Reservaciones pendientes de confirmación
        </h2>
        <p>Estas solicitudes ya pagaron y esperan que el restaurante las confirme.</p>
      </header>
      <ul>
        <li v-for="reservation in pendingReservations" :key="reservation.id">
          <div>
            <strong>{{ reservation.name }}</strong>
            <span>{{ formatAdminDate(reservation.date) }} · {{ formatTime(reservation.time) }}</span>
          </div>
          <button class="link-btn" @click="selectReservationForAction(reservation.id)">Ver detalle</button>
        </li>
      </ul>
    </section>

    <section class="pending-section walkin-section">
      <header>
        <h2>Lista de espera walk-ins</h2>
        <p>Se muestran por orden de llegada y no alteran el flujo de reservaciones del landing.</p>
      </header>
      <div v-if="activeWalkIns.length" class="walkin-layout">
        <div class="walkin-grid">
          <article
            v-for="walkIn in activeWalkIns"
            :key="walkIn.id"
            :class="['walkin-card', walkIn.status === 'seated' ? 'is-seated' : 'is-queued', { 'is-selected': selectedWalkInId === walkIn.id }]"
          >
            <header class="walkin-card__header">
              <div class="walkin-card__identity">
                <p class="walkin-card__eyebrow">Turno #{{ walkIn.queueNumber || '--' }}</p>
                <h3>{{ walkIn.name }}</h3>
                <p class="walkin-card__meta">Llegó {{ formatTime(walkIn.arrivalTime) }}</p>
              </div>
              <span class="status-pill">{{ walkIn.status }}</span>
            </header>

            <div class="walkin-card__summary">
              <span>{{ walkIn.people }} {{ walkIn.people === 1 ? 'persona' : 'personas' }}</span>
              <span v-if="walkIn.phone">{{ walkIn.phone }}</span>
              <span v-if="walkIn.tableNumber">Mesa {{ walkIn.tableNumber }}</span>
              <span v-else>Sin mesa</span>
            </div>

            <div class="action-links">
              <button
                class="card-action-btn card-action-btn--primary"
                @click="seatWalkIn(walkIn)"
                :disabled="loading"
              >
                Atendido
              </button>
              <button
                class="card-action-btn card-action-btn--danger-soft"
                @click="cancelWalkIn(walkIn)"
                :disabled="loading || walkIn.status === 'cancelled'"
              >
                Cancelar
              </button>
            </div>

            <button
              type="button"
              class="walkin-card__detail-toggle"
              @click="toggleWalkInDetails(walkIn.id)"
            >
              {{ selectedWalkInId === walkIn.id ? 'Ocultar detalle' : 'Ver detalle' }}
            </button>
          </article>
        </div>

        <aside v-if="selectedWalkIn" class="walkin-drawer">
          <div class="walkin-drawer__header">
            <div>
              <p class="walkin-drawer__eyebrow">Detalle de walk-in</p>
              <h3>{{ selectedWalkIn.name }}</h3>
              <p class="walkin-drawer__meta">
                Turno #{{ selectedWalkIn.queueNumber || '--' }} · {{ formatAdminDate(selectedWalkIn.arrivalDate) }} · {{ formatTime(selectedWalkIn.arrivalTime) }}
              </p>
            </div>
            <button type="button" class="walkin-drawer__close" @click="closeWalkInDetails">×</button>
          </div>

          <div class="walkin-drawer__grid">
            <article class="meta-panel">
              <span class="meta-label">Estado</span>
              <strong>{{ selectedWalkIn.status }}</strong>
            </article>
            <article class="meta-panel">
              <span class="meta-label">Personas</span>
              <strong>{{ selectedWalkIn.people }}</strong>
            </article>
            <article class="meta-panel">
              <span class="meta-label">Teléfono</span>
              <strong>{{ selectedWalkIn.phone || 'Sin teléfono' }}</strong>
            </article>
            <article class="meta-panel">
              <span class="meta-label">Email</span>
              <strong>{{ selectedWalkIn.email || 'Sin email' }}</strong>
            </article>
            <article class="meta-panel">
              <span class="meta-label">Mesa</span>
              <strong>{{ selectedWalkIn.tableNumber || 'Sin mesa' }}</strong>
            </article>
            <article class="meta-panel">
              <span class="meta-label">Email QR</span>
              <strong>{{ selectedWalkIn.emailStatus || 'pendiente' }}</strong>
            </article>
          </div>

          <p v-if="selectedWalkIn.specialNotes" class="notes">
            <span class="notes-label">Comentarios</span>
            {{ selectedWalkIn.specialNotes }}
          </p>

          <div class="action-links">
            <button
              class="card-action-btn card-action-btn--primary"
              @click="seatWalkIn(selectedWalkIn)"
              :disabled="loading"
            >
              Atendido
            </button>
            <button
              class="card-action-btn card-action-btn--danger-soft"
              @click="cancelWalkIn(selectedWalkIn)"
              :disabled="loading || selectedWalkIn.status === 'cancelled'"
            >
              Cancelar
            </button>
          </div>
        </aside>
      </div>
      <p v-else class="note">No hay walk-ins activos en lista de espera para esa fecha.</p>
    </section>

    <section class="reservation-grid">
      <article
        v-for="reservation in filteredReservations"
        :key="reservation.id"
        :id="'reservation-' + reservation.id"
        :class="['reservation-card', getStatusClass(reservation.status)]"
      >
        <header class="reservation-card__header">
          <div class="reservation-card__identity">
            <p class="reservation-card__eyebrow">{{ formatAdminDate(reservation.date) }}</p>
            <h3>{{ reservation.name }}</h3>
            <p class="reservation-card__contact">{{ reservation.email }}</p>
          </div>
          <span class="status-pill">{{ reservation.status }}</span>
        </header>
        <div class="reservation-card__meta-grid">
          <div class="meta-panel">
            <span class="meta-label">Hora</span>
            <strong>{{ formatTime(reservation.time) }}</strong>
          </div>
          <div class="meta-panel">
            <span class="meta-label">Personas</span>
            <strong>{{ reservation.people }}</strong>
          </div>
          <div class="meta-panel" v-if="reservation.phone">
            <span class="meta-label">Teléfono</span>
            <strong>{{ reservation.phone }}</strong>
          </div>
          <div class="meta-panel" v-if="reservation.paymentRequired">
            <span class="meta-label">Pago Clover</span>
            <strong>{{ reservation.paymentStatus || 'awaiting_payment' }}</strong>
          </div>
          <div class="meta-panel" v-if="reservation.paymentRequired && reservation.paymentStatus === 'paid' && reservation.cloverPaymentId">
            <span class="meta-label">Payment ID</span>
            <strong>{{ reservation.cloverPaymentId }}</strong>
          </div>
          <div class="meta-panel" v-if="reservation.paymentRequired && reservation.paymentStatus === 'paid' && reservation.cloverOrderId">
            <span class="meta-label">Order ID</span>
            <strong>{{ reservation.cloverOrderId }}</strong>
          </div>
          <div class="meta-panel" v-if="reservation.paymentRequired && reservation.paymentStatus === 'paid' && reservation.paymentCapturedAmount">
            <span class="meta-label">Cobrado</span>
            <strong>{{ formatReservationPaymentAmount(reservation) }}</strong>
          </div>
          <div class="meta-panel" v-if="reservation.tableNumber">
            <span class="meta-label">Mesa</span>
            <strong>{{ reservation.tableNumber }}</strong>
          </div>
          <div class="meta-panel" v-if="reservation.tableGroupName">
            <span class="meta-label">Grupo</span>
            <strong>{{ reservation.tableGroupName }}</strong>
          </div>
        </div>
        <div
          v-if="reservation.paymentRequired && reservation.paymentStatus === 'paid'"
          :class="['deposit-banner', reservation.depositApplied ? 'deposit-banner--done' : 'deposit-banner--pending']"
        >
          <div class="deposit-banner__info">
            <span class="deposit-banner__icon">{{ reservation.depositApplied ? '✓' : '💳' }}</span>
            <div>
              <p class="deposit-banner__label">{{ reservation.depositApplied ? 'Depósito aplicado al ticket' : 'Depósito pendiente de aplicar' }}</p>
              <p class="deposit-banner__amount">{{ formatReservationPaymentAmount(reservation) }}</p>
            </div>
          </div>
          <button
            v-if="!reservation.depositApplied"
            class="deposit-banner__btn"
            :disabled="loading"
            @click="markDepositApplied(reservation)"
          >
            Marcar como aplicado
          </button>
          <span v-else class="deposit-banner__applied-at">
            {{ reservation.depositAppliedAt ? new Date(reservation.depositAppliedAt).toLocaleTimeString('es-PR', { hour: '2-digit', minute: '2-digit' }) : '' }}
          </span>
        </div>

        <p class="notes" v-if="reservation.specialNotes">
          <span class="notes-label">Comentarios del cliente</span>
          {{ reservation.specialNotes }}
        </p>
        <p class="notes" v-if="reservation.cancellationReason">
          <span class="notes-label">Motivo de cancelación</span>
          {{ reservation.cancellationReason }}
        </p>

        <div v-if="reservation.status !== 'cancelled'" class="actions-column">
          <div class="action-buttons">
            <button
              class="card-action-btn card-action-btn--pill card-action-btn--primary"
              @click="confirmReservation(reservation)"
              :disabled="loading || reservation.status === 'confirmed' || (reservation.paymentRequired && reservation.paymentStatus !== 'paid')"
            >
              <template v-if="reservation.status === 'confirmed'">Confirmada</template>
              <template v-else>Confirmar</template>
            </button>
            <button class="card-action-btn card-action-btn--pill card-action-btn--warning" @click="denyReservation(reservation)" :disabled="loading">
              Denegar
            </button>
            <button class="card-action-btn card-action-btn--pill card-action-btn--danger" @click="cancelReservation(reservation)" :disabled="loading">
              Cancelar
            </button>
          </div>

          <div class="action-links">
            <button class="card-action-btn card-action-btn--secondary" @click="addManualTable(reservation)">
              {{ reservation.tableNumber ? 'Editar mesa' : 'Añadir mesa' }}
            </button>
            <button class="card-action-btn card-action-btn--secondary" @click="openEditModal(reservation)">Editar</button>
            <button class="card-action-btn card-action-btn--qr" @click="resendConfirmationEmail(reservation)" :disabled="loading">Reenviar QR</button>
            <button class="card-action-btn card-action-btn--danger-soft" @click="deleteReservation(reservation)" :disabled="loading">Eliminar</button>
          </div>
        </div>
      </article>
    </section>

    <div v-if="showReservationModal" class="modal">
      <div class="modal-content">
        <AdminReservationView @reservation-complete="closeReservationModal" />
        <button class="btn ghost" @click="closeReservationModal">Cerrar</button>
      </div>
    </div>

    <div v-if="showScannedReservationsModal" class="modal" @click.self="closeScannedReservationsModal">
      <div class="modal-content">
        <button class="modal-close" @click="closeScannedReservationsModal">×</button>
        <ReservationHistoryView @close="closeScannedReservationsModal" />
        <button class="btn ghost" @click="closeScannedReservationsModal">Cerrar</button>
      </div>
    </div>

    <div v-if="showQRCodeModal" class="modal">
      <div class="modal-content">
        <QRCodeView :reservation="selectedReservation" @close="closeQRCodeModal" />
        <button class="btn ghost" @click="closeQRCodeModal">Cerrar</button>
      </div>
    </div>

    <div v-if="showScheduleModal" class="modal">
      <div class="modal-content schedule">
        <header>
          <h3>Gestión de horarios</h3>
          <p>Define los horarios base y controla la visibilidad del cupo por hora según necesidad.</p>
        </header>
        <div class="schedule-tabs">
          <button
            :class="{ active: scheduleTab === 'base' }"
            @click="scheduleTab = 'base'"
          >
            Configurar horarios base
          </button>
          <button
            :class="{ active: scheduleTab === 'dates' }"
            @click="scheduleTab = 'dates'"
          >
            Gestionar fechas específicas
          </button>
        </div>

        <div v-if="scheduleTab === 'base'" class="schedule-base">
          <div class="weekly-day-manager">
            <div class="manager-header">
              <p class="field-label">Plantillas de días visibles para el cliente.</p>
              <div class="new-day-form">
                <input
                  type="text"
                  v-model="newDayLabel"
                  placeholder="Nuevo día (ej. VIP viernes)"
                />
                <button class="btn primary" type="button" @click="addWeeklyDay" :disabled="!newDayLabel.trim()">
                  Añadir día
                </button>
              </div>
            </div>
            <div class="weekly-day-selector modern">
              <div
                v-for="day in weeklyDayOptions"
                :key="day"
                :class="['day-pill', { active: selectedWeeklyDay === day }]"
              >
                <button type="button" @click="selectWeeklyDay(day)">
                  {{ getDayLabel(day) }}
                </button>
                <div class="day-actions" v-if="selectedWeeklyDay === day">
                  <button type="button" class="link-btn" @click="startEditDay(day)">Editar</button>
                  <button
                    type="button"
                    class="link-btn danger"
                    v-if="weeklyDayOptions.length > 1"
                    @click="deleteWeeklyDay(day)"
                  >
                    Eliminar
                  </button>
                </div>
                <div v-if="editingDayKey === day" class="day-edit">
                  <input type="text" v-model="editingDayLabel" placeholder="Nombre del día" />
                  <button class="btn primary" type="button" @click="saveDayLabel">Guardar</button>
                  <button class="btn ghost" type="button" @click="cancelEditDay">Cancelar</button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="selectedWeeklyDay && weeklyTemplates[selectedWeeklyDay]?.length" class="time-list">
            <article
              v-for="time in weeklyTemplates[selectedWeeklyDay]"
              :key="`${selectedWeeklyDay}-${time}`"
              class="time-card"
            >
              <div class="time-info" v-if="!(editingWeeklyTime?.dayKey === selectedWeeklyDay && editingWeeklyTime?.originalTime === time)">
                <div>
                  <p class="time-label">{{ formatTimeTo12Hour(time) }}</p>
                  <p class="time-sub">Activo semanalmente para este día</p>
                </div>
                <div class="time-actions">
                  <button class="btn ghost" type="button" @click="startEditWeeklyTime(selectedWeeklyDay, time)">Editar</button>
                  <button class="btn danger ghost" type="button" @click="removeWeeklyTime(selectedWeeklyDay, time)">Eliminar</button>
                </div>
              </div>
              <div v-else class="time-edit-row">
                <input
                  type="text"
                  v-model="editingWeeklyTimeValue"
                  placeholder="HH:MM"
                />
                <div class="time-actions">
                  <button class="btn primary" type="button" @click="saveWeeklyTimeEdit">Guardar</button>
                  <button class="btn ghost" type="button" @click="cancelEditWeeklyTime">Cancelar</button>
                </div>
              </div>
            </article>
          </div>
          <p v-else class="note">Selecciona un día para gestionar sus horarios base.</p>
          <div class="custom-time-form">
            <input
              type="text"
              v-model="customWeeklyTime"
              placeholder="Añadir horario manual (HH:MM)"
            />
            <button class="btn ghost" type="button" @click="addCustomWeeklyTime" :disabled="!customWeeklyTime.trim()">
              Añadir horario
            </button>
          </div>
          <p class="note">
            Usa los toggles para activar o desactivar cada horario base. Los cambios se reflejan inmediatamente en la vista del cliente.
          </p>
          <p class="note muted">La configuración de cobros se gestiona ahora desde el panel principal.</p>
        </div>

        <div v-else class="schedule-dates">
          <div class="schedule-dates-header">
            <div class="date-picker">
              <label class="field-label">Selecciona una fecha específica</label>
              <input type="date" v-model="selectedDate" @change="loadTimesForDate" />
            </div>
            <div class="quick-date-actions">
              <p>Usa los accesos rápidos para saltar al próximo fin de semana.</p>
              <div class="quick-buttons">
                <button class="btn ghost" @click="jumpToNextWeekend('friday')">Próximo Viernes</button>
                <button class="btn ghost" @click="jumpToNextWeekend('saturday')">Próximo Sábado</button>
                <button class="btn ghost" @click="jumpToNextWeekend('sunday')">Próximo Domingo</button>
              </div>
            </div>
          </div>
          <div v-if="selectedDate" class="schedule-grid">
            <h4>Horarios habilitados para {{ selectedDate }}</h4>
            <div class="flow-time-grid">
              <div
                v-for="time in allPossibleTimes"
                :key="`date-${selectedDate}-${time}`"
                :class="['flow-toggle', { active: !isTimeDisabled(selectedDate, time) }]"
              >
                <div>
                  <p class="time-label">{{ formatTimeTo12Hour(time) }}</p>
                  <p class="time-sub" v-if="!isTimeDisabled(selectedDate, time)">Visible para clientes</p>
                  <p class="time-sub" v-else>Bloqueado para esta fecha</p>
                </div>
                <label class="flow-switch">
                  <input
                    type="checkbox"
                    :checked="!isTimeDisabled(selectedDate, time)"
                    @change="toggleTime(selectedDate, time)"
                  />
                  <span class="slider"></span>
                </label>
              </div>
            </div>
            <p class="note">
              Estas anulaciones respetan los horarios base pero permiten apagar horas puntuales sin perder la plantilla semanal.
            </p>
          </div>
        </div>

        <button class="btn ghost" @click="closeScheduleModal">Cerrar</button>
      </div>
    </div>

    <div v-if="showPaymentSettingsModal" class="modal" @click.self="closePaymentSettingsModal">
      <div class="modal-content">
        <CloverPaymentSettingsModal
          :settings="paymentSettingsConfig"
          :webhook-status="cloverWebhookStatus"
          :refreshing-status="loadingCloverWebhookStatus"
          @close="closePaymentSettingsModal"
          @refresh-status="loadCloverWebhookStatus"
          @saved="handlePaymentSettingsSaved"
        />
      </div>
    </div>

    <div v-if="showWalkInModal" class="modal" @click.self="closeWalkInModal">
      <div class="modal-content">
        <button class="modal-close" @click="closeWalkInModal">×</button>
        <AdminWalkInModal @saved="handleWalkInSaved" @close="closeWalkInModal" />
      </div>
    </div>

    <div v-if="showCalendarModal" class="modal" @click.self="closeCalendarModal">
      <div class="modal-content">
        <button class="modal-close" @click="closeCalendarModal">×</button>
        <ReservationCalendar />
      </div>
    </div>

    <div v-if="showFailedPaymentsModal" class="modal" @click.self="closeFailedPaymentsModal">
      <div class="modal-content modal-content--incidents">
        <button class="modal-close" @click="closeFailedPaymentsModal">×</button>
        <header class="failed-payments-section__header">
          <div>
            <h2>⚠️ Pagos fallidos</h2>
            <p>Clover no pudo completar estos cobros. El cliente no fue cargado. Notifica al cliente o cancela la reservación.</p>
          </div>
        </header>
        <ul class="failed-payments-list">
          <li
            v-for="r in failedPaymentReservations"
            :key="r.id"
            class="failed-payment-card"
          >
            <div class="failed-payment-card__main">
              <div class="failed-payment-card__identity">
                <strong>{{ r.name }}</strong>
                <span>{{ r.email }}</span>
                <span v-if="r.phone">{{ r.phone }}</span>
              </div>
              <div class="failed-payment-card__details">
                <span class="failed-payment-detail">
                  <span class="failed-payment-detail__label">Fecha</span>
                  <strong>{{ formatAdminDate(r.date) }}</strong>
                </span>
                <span class="failed-payment-detail">
                  <span class="failed-payment-detail__label">Hora</span>
                  <strong>{{ formatTime(r.time) }}</strong>
                </span>
                <span class="failed-payment-detail">
                  <span class="failed-payment-detail__label">Personas</span>
                  <strong>{{ r.people }}</strong>
                </span>
                <span class="failed-payment-detail">
                  <span class="failed-payment-detail__label">Monto no cobrado</span>
                  <strong>{{ formatReservationPaymentAmount(r) }}</strong>
                </span>
              </div>
              <div class="failed-payment-card__reason" v-if="r.paymentFailedReason">
                <span class="failed-payment-card__reason-label">Por qué falló</span>
                <p>{{ r.paymentFailedReason }}</p>
              </div>
              <div class="failed-payment-card__reason failed-payment-card__reason--technical" v-if="r.paymentFailedVoidReason">
                <span class="failed-payment-card__reason-label">Código Clover</span>
                <code>{{ r.paymentFailedVoidReason }}{{ r.paymentFailedVoidDetail ? ` · ${r.paymentFailedVoidDetail}` : '' }}</code>
              </div>
              <p class="failed-payment-card__time" v-if="r.paymentFailedAt">
                Falló: {{ new Date(r.paymentFailedAt).toLocaleString('es-PR', { timeZone: 'America/Puerto_Rico' }) }}
              </p>
            </div>
            <div class="failed-payment-card__actions">
              <button
                class="card-action-btn card-action-btn--pill card-action-btn--primary"
                @click="retryFailedPaymentEmail(r)"
                :disabled="loading"
              >
                Notificar cliente
              </button>
              <button
                class="card-action-btn card-action-btn--pill card-action-btn--danger"
                @click="cancelFailedPaymentReservation(r)"
                :disabled="loading"
              >
                Cancelar reservación
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="showPaymentIncidentsModal" class="modal" @click.self="closePaymentIncidentsModal">
      <div class="modal-content modal-content--incidents">
        <button class="modal-close" @click="closePaymentIncidentsModal">×</button>
        <header class="failed-payments-section__header">
          <div>
            <h2>Incidentes de pago</h2>
            <p>Pagos fallidos, auto-voids y reembolsos detectados en tiempo real.</p>
          </div>
        </header>
        <ul class="failed-payments-list">
          <li
            v-for="incident in paymentIncidents"
            :key="incident.id"
            class="failed-payment-card"
          >
            <div class="failed-payment-card__main">
              <div class="failed-payment-card__identity">
                <strong>{{ incident.reservation.name }}</strong>
                <span>{{ incident.reservation.email }}</span>
                <span v-if="incident.reservation.phone">{{ incident.reservation.phone }}</span>
              </div>
              <div class="failed-payment-card__details">
                <span class="failed-payment-detail">
                  <span class="failed-payment-detail__label">Fecha</span>
                  <strong>{{ formatAdminDate(incident.reservation.date) }}</strong>
                </span>
                <span class="failed-payment-detail">
                  <span class="failed-payment-detail__label">Hora</span>
                  <strong>{{ formatTime(incident.reservation.time) }}</strong>
                </span>
                <span class="failed-payment-detail">
                  <span class="failed-payment-detail__label">Monto</span>
                  <strong>{{ incident.amountLabel }}</strong>
                </span>
                <span class="failed-payment-detail">
                  <span class="failed-payment-detail__label">Estado</span>
                  <strong>{{ incident.statusLabel }}</strong>
                </span>
              </div>
              <div class="failed-payment-card__reason">
                <span class="failed-payment-card__reason-label">Incidente</span>
                <p>{{ incident.summary }}</p>
              </div>
              <div
                class="failed-payment-card__reason failed-payment-card__reason--technical"
                v-if="incident.technicalLabel"
              >
                <span class="failed-payment-card__reason-label">{{ incident.technicalLabel }}</span>
                <code>{{ incident.technicalValue }}</code>
              </div>
              <div
                class="failed-payment-card__reason failed-payment-card__reason--technical"
                v-if="incident.referenceLine"
              >
                <span class="failed-payment-card__reason-label">Referencias Clover</span>
                <code>{{ incident.referenceLine }}</code>
              </div>
              <p class="failed-payment-card__time" v-if="incident.detectedAt">
                Detectado: {{ formatIncidentTimestamp(incident.detectedAt) }}
              </p>
              <p class="failed-payment-card__time">
                {{ incident.notificationLine }}
              </p>
            </div>
            <div class="failed-payment-card__actions">
              <span v-if="incident.kind === 'payment_voided'" class="badge badge--refunded">Void recuperable</span>
              <span v-else-if="incident.kind === 'payment_refunded'" class="badge badge--refunded">Reembolso</span>
              <span v-else class="status-pill">Pago fallido</span>
              <button
                v-if="incident.canRetryEmail"
                class="card-action-btn card-action-btn--pill card-action-btn--primary"
                @click="retryPaymentIncidentEmail(incident)"
                :disabled="loading"
              >
                Reenviar email de retry
              </button>
              <button
                class="card-action-btn card-action-btn--pill card-action-btn--secondary"
                @click="selectReservationForAction(incident.reservationId); closePaymentIncidentsModal()"
                :disabled="loading"
              >
                Ver reservación
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <video ref="videoRef" v-if="scanning" class="qr-video"></video>

    <div v-if="showEditModal" class="modal">
      <div class="modal-content">
        <h2>Editar reservación</h2>
        <form @submit.prevent="updateReservation(selectedReservation)">
          <div>
            <label for="editName">Nombre:</label>
            <input type="text" v-model="selectedReservation.name" id="editName" required>
          </div>
          <div>
            <label for="editEmail">Email:</label>
            <input type="email" v-model="selectedReservation.email" id="editEmail" required>
          </div>
          <div>
            <label for="editPhone">Teléfono:</label>
            <input type="tel" v-model="selectedReservation.phone" id="editPhone" required>
          </div>
          <div>
            <label for="editDate">Fecha:</label>
            <input type="date" v-model="selectedReservation.date" id="editDate" required>
          </div>
          <div>
            <label for="editTime">Hora:</label>
            <input type="time" v-model="selectedReservation.time" id="editTime" required>
          </div>
          <div>
            <label for="editPeople">Número de personas:</label>
            <input type="number" v-model.number="selectedReservation.people" id="editPeople" required min="1">
          </div>
          <div>
            <label for="editTable">Número de mesa:</label>
            <input type="text" v-model="selectedReservation.tableNumber" id="editTable" />
          </div>
          <button type="submit" class="btn primary">Actualizar</button>
          <button type="button" class="btn ghost" @click="closeEditModal">Cancelar</button>
        </form>
      </div>
    </div>

  </div>
</template>


<script>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';

import { db } from '../firebase/init';
import QrScanner from 'qr-scanner';
import AdminReservationView from './AdminReservationView.vue';
import AdminWalkInModal from '../components/AdminWalkInModal.vue';
import QRCodeView from './QRCodeView.vue';
import ReservationHistoryView from './ReservationHistoryView.vue';
import ReservationCalendar from './ReservationCalendar.vue';
import CloverPaymentSettingsModal from '../components/CloverPaymentSettingsModal.vue';
import logoImage from '../assets/logo.png';
import { parseLocalDate } from '../utils/date';
import { formatTimeLabel } from '../utils/time';
import { getCloverWebhookStatus, updateReservationAdminConfig } from '../services/api/adminConfig';
import { useAdminSchedule } from '../composables/useAdminSchedule';
import {
  getAdminISODate,
  getAdminNow,
  toAdminDateTime,
  normalizePhone,
  getWaiterName,
  matchesSection,
  matchesGroupSection,
  tableCanBeUsedForReservation,
  groupCanBeUsedForReservation,
  calculateQuickGroupCapacity,
  extractScannedId
} from '../utils/adminPanel';
import {
  seatWalkIn as seatWalkInRequest,
  cancelWalkIn as cancelWalkInRequest,
  confirmReservation as confirmReservationRequest,
  resendReservationConfirmation as resendReservationConfirmationRequest,
  denyReservation as denyReservationRequest,
  cancelReservation as cancelReservationRequest,
  updateReservation as updateReservationRequest,
  scanReservationQr as scanReservationQrRequest,
  assignReservationTable as assignReservationTableRequest,
  releaseReservationTable as releaseReservationTableRequest,
  assignReservationGroup as assignReservationGroupRequest,
  releaseReservationGroup as releaseReservationGroupRequest,
  assignReservationWaiter as assignReservationWaiterRequest,
  setReservationManualTable as setReservationManualTableRequest,
  deleteReservation as deleteReservationRequest,
  markDepositApplied as markDepositAppliedRequest,
  cleanOldReservations as cleanOldReservationsRequest,
  purgePendingReservations as purgePendingReservationsRequest
} from '../services/api/adminReservations';
import {
  retryPaymentIncidentNotification as retryPaymentIncidentNotificationRequest,
  backfillHistoricalVoidIncidents as backfillHistoricalVoidIncidentsRequest
} from '../services/api/admin';
import {
  createTable as createTableRequest,
  updateTable as updateTableRequest,
  deleteTable as deleteTableRequest,
  createWaiter as createWaiterRequest,
  deleteWaiter as deleteWaiterRequest,
  createSection as createSectionRequest,
  deleteSection as deleteSectionRequest,
  createTableGroup as createTableGroupRequest,
  releaseTableGroup as releaseTableGroupRequest,
  deleteTableGroup as deleteTableGroupRequest
} from '../services/api/adminResources';

QrScanner.WORKER_PATH = new URL('qr-scanner/qr-scanner-worker.min.js', import.meta.url).toString();

const WEEKDAY_TIME_OPTIONS = [
  '12:00', '12:30', '13:00', '13:30', '14:45',
  '15:00', '15:30', '16:00', '16:30', '17:45',
  '18:00', '18:30', '19:00', '19:30'
];

const SUNDAY_TIME_OPTIONS = [
  '12:00', '12:30', '13:00', '13:30', '14:45',
  '15:00', '15:30', '16:00', '16:30', '17:45'
];

const WEEKLY_DAY_LABELS = {
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo'
};

const createDefaultWeeklyTemplates = () => ({
  friday: [...WEEKDAY_TIME_OPTIONS],
  saturday: [...WEEKDAY_TIME_OPTIONS],
  sunday: [...SUNDAY_TIME_OPTIONS]
});

export default {
  name: 'AdminPanel',
  components: {
    AdminReservationView,
    AdminWalkInModal,
    QRCodeView,
    ReservationHistoryView,
    ReservationCalendar,
    CloverPaymentSettingsModal
  },
  setup() {
    const searchQuery = ref('');
    const reservations = ref([]);
    const walkIns = ref([]);
    const filteredReservations = ref([]);
    const pendingReservations = ref([]);

    const INVALID_STATUSES = ['cancelled', 'denied'];
    const INVALID_PAYMENT_STATUSES = ['payment_failed', 'payment_voided'];

    const isConfirmedReservation = (r) =>
      r.status === 'confirmed' &&
      !INVALID_PAYMENT_STATUSES.includes(r.paymentStatus);

    const confirmedReservations = computed(() =>
      reservations.value.filter(isConfirmedReservation)
    );
    const newReservationAlert = ref(false);
    const selectedReservation = ref(null);
    const showEditModal = ref(false);
    const showReservationModal = ref(false);
    const showScannedReservationsModal = ref(false);
    const loading = ref(false);
    const scanning = ref(false);
    const videoRef = ref(null);
    const qrScannerInstance = ref(null);
    const showQRCodeModal = ref(false);
    const scannedReservationId = ref(null);
    const selectedWalkInId = ref('');
    const showTablesModal = ref(false);
    const showWalkInModal = ref(false);
    const showFailedPaymentsModal = ref(false);
    const showPaymentIncidentsModal = ref(false);
    const tablesModalTab = ref('overview');
    const tableTabOptions = [
      { id: 'overview', label: 'Resumen' },
      { id: 'tables', label: 'Mesas' },
      { id: 'waiters', label: 'Meseros' },
      { id: 'sections', label: 'Secciones' },
      { id: 'groups', label: 'Grupos' }
    ];
    const specialNotes = ref('');
    const tables = ref([]);
    const tableGroups = ref([]);
    const availableWaiters = ref([]);
    const tableSections = ref([]);
    const selectedTableSection = ref('all');
    const selectedTableStatus = ref('all');
    const tableSearch = ref('');
    const newTableForm = ref({
      section: '',
      number: '',
      capacity: 4,
      waiter: ''
    });
    const editingTableId = ref(null);
    const editTableData = ref({
      section: '',
      number: '',
      capacity: 4,
      waiter: ''
    });
    const newWaiterName = ref('');
    const newSectionName = ref('');
    const newGroupName = ref('');
    const newGroupTables = ref([]);
    const groupSectionFilter = ref('all');
    const cleaningOldReservations = ref(false);
    const backfillingHistoricalVoids = ref(false);
    const cleanedReservationsCount = ref(0);
    const paymentEnabled = ref(false);
    const paymentMode = ref('deposit');
    const paymentAmount = ref(0);
    const serviceFeeAmount = ref(0);
    const paymentCurrency = ref('USD');
    const paymentOverrides = ref([]);
    const autoConfirmAfterPayment = ref(false);
    const paymentProvider = ref('clover');
    const showPaymentSettingsModal = ref(false);
    const cloverWebhookStatus = ref(null);
    const loadingCloverWebhookStatus = ref(false);
    const adminConfigRef = doc(db, 'adminConfig', 'reservations');
    const hourlySummaryDate = ref(getAdminISODate());
    const {
      disabledTimes,
      selectedDate,
      showScheduleModal,
      showCalendarModal,
      scheduleTab,
      selectedWeeklyDay,
      weeklyTemplates,
      weeklyDayLabels,
      customWeeklyTime,
      newDayLabel,
      editingDayKey,
      editingDayLabel,
      editingWeeklyTime,
      editingWeeklyTimeValue,
      weeklyDayOptions,
      getDayLabel,
      selectWeeklyDay,
      addWeeklyDay,
      startEditDay,
      cancelEditDay,
      saveDayLabel,
      deleteWeeklyDay,
      startEditWeeklyTime,
      cancelEditWeeklyTime,
      saveWeeklyTimeEdit,
      removeWeeklyTime,
      isTimeDisabled,
      toggleTime,
      addCustomWeeklyTime,
      jumpToNextWeekend,
      loadTimesForDate,
      openScheduleModal,
      closeScheduleModal,
      openCalendarModal,
      closeCalendarModal,
      applyRemoteConfig
    } = useAdminSchedule({
      updateConfig: updateReservationAdminConfig,
      createDefaultWeeklyTemplates,
      weeklyDayLabelsBase: WEEKLY_DAY_LABELS,
      getBaseTimesForDay: (day) => (day === 'sunday' ? SUNDAY_TIME_OPTIONS : WEEKDAY_TIME_OPTIONS),
      getAdminNow
    });
    const allPossibleTimes = [
  '12:00', '12:30', '13:00', '13:30', '14:45', '15:00',
  '15:30',  '16:00', '16:30', '17:45', '18:00', '18:30',
  '19:00', '19:30',
];

    const formatTimeTo12Hour = (time) => formatTimeLabel(time);
    const adminDateFormatter = new Intl.DateTimeFormat('es-PR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const formatAdminDate = (date) => {
      const parsed = parseLocalDate(date);
      return parsed ? adminDateFormatter.format(parsed) : date;
    };
    const formattedPaymentAmount = computed(() => (
      new Intl.NumberFormat('es-PR', {
        style: 'currency',
        currency: paymentCurrency.value || 'USD'
      }).format((Number(paymentAmount.value) || 0) / 100)
    ));
    const formatReservationPaymentAmount = (reservation) => {
      const amount = Number(reservation?.paymentCapturedAmount || reservation?.paymentAmount || 0) / 100;
      return new Intl.NumberFormat('es-PR', {
        style: 'currency',
        currency: String(reservation?.paymentCapturedCurrency || reservation?.paymentCurrency || 'USD').trim().toUpperCase() || 'USD'
      }).format(amount);
    };
    const formatCurrencyAmount = (amountInCents, currencyCode = 'USD') => (
      new Intl.NumberFormat('es-PR', {
        style: 'currency',
        currency: String(currencyCode || 'USD').trim().toUpperCase() || 'USD'
      }).format((Number(amountInCents) || 0) / 100)
    );
    const formatIncidentTimestamp = (value) => (
      new Date(value).toLocaleString('es-PR', { timeZone: 'America/Puerto_Rico' })
    );
    const getPaymentIncidentTimestamp = (value) => {
      const rawValue = value?.lastDetectedAt
        || value?.detectedAt
        || value?.paymentFailedAt
        || value?.refundedAt
        || value?.updatedAt
        || value?.createdAt
        || '';
      const timestamp = Date.parse(rawValue);
      return Number.isFinite(timestamp) ? timestamp : 0;
    };
    const isOpenPaymentIncidentReservation = (reservation) => {
      if (!reservation?.id) {
        return false;
      }
      return true;
    };
    const sortPaymentIncidentReservations = (a, b) => (
      getPaymentIncidentTimestamp(b) - getPaymentIncidentTimestamp(a)
      || toAdminDateTime(a.date, a.time) - toAdminDateTime(b.date, b.time)
    );
    const buildRealtimePaymentIncident = (incident) => {
      const incidentType = String(incident?.incidentType || '').trim().toLowerCase();
      const technicalValue = incidentType === 'payment_refunded'
        ? String(incident?.refundReason || '').trim()
        : [incident?.voidReason, incident?.voidReasonDetail].filter(Boolean).join(' · ');
      const canRetryEmail = incident?.retryEligible !== false;
      const reservation = {
        id: incident?.reservationId,
        name: incident?.clientName || '',
        email: incident?.clientEmail || '',
        phone: incident?.clientPhone || '',
        date: incident?.reservationDate || '',
        time: incident?.reservationTime || '',
        people: Number(incident?.people || 0) || 0,
        paymentAmount: Number(incident?.amountCents || 0) || 0,
        paymentCurrency: incident?.currency || 'USD',
        paymentFailedEmailStatus: incident?.customerNotificationStatus || '',
        paymentFailedEmailSentAt: incident?.customerNotificationSentAt || '',
        paymentFailedEmailError: incident?.customerNotificationError || '',
        cloverCheckoutId: incident?.cloverCheckoutId || '',
        cloverPaymentId: incident?.cloverPaymentId || '',
        cloverOrderId: incident?.cloverOrderId || ''
      };

      return {
        id: incident.id,
        reservationId: incident?.reservationId || '',
        reservation,
        kind: incidentType === 'payment_voided'
          ? 'payment_voided'
          : incidentType === 'payment_refunded'
            ? 'payment_refunded'
            : 'payment_failed',
        canRetryEmail,
        detectedAt: incident?.lastDetectedAt || incident?.detectedAt || incident?.updatedAt || '',
        amountLabel: formatCurrencyAmount(incident?.amountCents || 0, incident?.currency || 'USD'),
        statusLabel: incidentType === 'payment_voided'
          ? 'Auto-void detectado'
          : incidentType === 'payment_refunded'
            ? 'Reembolso detectado'
            : 'Pago fallido',
        summary: incident?.reasonLabel || incident?.note || 'Incidente de pago detectado por Clover.',
        technicalLabel: technicalValue ? (incidentType === 'payment_refunded' ? 'Motivo Clover' : 'Código Clover') : '',
        technicalValue,
        referenceLine: [
          incident?.cloverCheckoutId ? `Checkout ${incident.cloverCheckoutId}` : '',
          incident?.cloverPaymentId ? `Payment ${incident.cloverPaymentId}` : '',
          incident?.cloverOrderId ? `Order ${incident.cloverOrderId}` : '',
          incident?.cloverRefundId ? `Refund ${incident.cloverRefundId}` : ''
        ].filter(Boolean).join(' · '),
        notificationLine: incident?.customerNotificationStatus === 'sent' && incident?.customerNotificationSentAt
          ? `${canRetryEmail ? 'Email de retry enviado' : 'Email de notificación enviado'}: ${formatIncidentTimestamp(incident.customerNotificationSentAt)}`
          : incident?.customerNotificationStatus === 'error'
            ? `Error al enviar email: ${incident?.customerNotificationError || 'Sin detalle.'}`
            : canRetryEmail
              ? 'Email de retry pendiente o sin confirmación registrada.'
              : 'Email de notificación pendiente o sin confirmación registrada.',
        sortTimestamp: getPaymentIncidentTimestamp(incident)
      };
    };
    const paymentSettingsConfig = computed(() => ({
      paymentEnabled: paymentEnabled.value,
      paymentMode: paymentMode.value,
      paymentAmount: paymentAmount.value,
      serviceFeeAmount: serviceFeeAmount.value,
      paymentCurrency: paymentCurrency.value,
      paymentOverrides: paymentOverrides.value,
      autoConfirmAfterPayment: autoConfirmAfterPayment.value,
      paymentProvider: paymentProvider.value
    }));
    const paymentSettingsSummary = computed(() => {
      if (!paymentEnabled.value) {
        return 'desactivado';
      }

      return `${paymentMode.value === 'full' ? 'Pago completo' : 'Depósito'} ${formattedPaymentAmount.value}`;
    });
    const failedPaymentIncidentSource = ref([]);
    const refundedIncidentSource = ref([]);
    const failedPaymentReservations = computed(() => (
      failedPaymentIncidentSource.value
        .filter(isOpenPaymentIncidentReservation)
        .sort(sortPaymentIncidentReservations)
    ));
    const failedPaymentAlert = computed(() => failedPaymentReservations.value.length > 0);
    const refundedReservations = computed(() => (
      refundedIncidentSource.value
        .filter(isOpenPaymentIncidentReservation)
        .sort(sortPaymentIncidentReservations)
    ));
    const refundedAlert = computed(() => refundedReservations.value.length > 0);
    const realtimePaymentIncidentSource = ref([]);
    const paymentIncidents = computed(() => (
      realtimePaymentIncidentSource.value
        .filter((incident) => String(incident?.status || '').trim().toLowerCase() !== 'resolved')
        .map((incident) => buildRealtimePaymentIncident(incident))
        .sort((a, b) => b.sortTimestamp - a.sortTimestamp)
    ));
    const paymentIncidentAlert = computed(() => paymentIncidents.value.length > 0);

    const openPaymentSettingsModal = () => {
      loadCloverWebhookStatus();
      showPaymentSettingsModal.value = true;
    };
    const closePaymentSettingsModal = () => {
      showPaymentSettingsModal.value = false;
    };
    const handlePaymentSettingsSaved = (payload) => {
      paymentEnabled.value = Boolean(payload.paymentEnabled);
      paymentMode.value = ['deposit', 'full'].includes(payload.paymentMode) ? payload.paymentMode : 'deposit';
      paymentAmount.value = Number(payload.paymentAmount) || 0;
      serviceFeeAmount.value = Number(payload.serviceFeeAmount) || 0;
      paymentCurrency.value = String(payload.paymentCurrency || 'USD').trim().toUpperCase() || 'USD';
      paymentOverrides.value = Array.isArray(payload.paymentOverrides) ? payload.paymentOverrides : [];
      autoConfirmAfterPayment.value = false;
      paymentProvider.value = String(payload.paymentProvider || 'clover').trim() || 'clover';
      closePaymentSettingsModal();
      alert(payload.message || 'Configuración de cobro actualizada.');
    };
    const loadCloverWebhookStatus = async () => {
      loadingCloverWebhookStatus.value = true;
      try {
        cloverWebhookStatus.value = await getCloverWebhookStatus();
      } catch (error) {
        console.error('Error cargando estado Clover:', error);
      } finally {
        loadingCloverWebhookStatus.value = false;
      }
    };

const filteredTables = computed(() => {
  return tables.value.filter((table) => {
    const matchesSection = selectedTableSection.value === 'all' || table.section === selectedTableSection.value;
    const matchesStatus = selectedTableStatus.value === 'all' || table.status === selectedTableStatus.value;
    const matchesSearch = !tableSearch.value ||
      `${table.section || ''}${table.number || ''}`.toLowerCase().includes(tableSearch.value.toLowerCase());
    return matchesSection && matchesStatus && matchesSearch;
  });
});

const tableStats = computed(() => {
  const occupied = tables.value.filter((table) => table.status === 'assigned').length;
  return {
    total: tables.value.length,
    occupied,
    available: tables.value.length - occupied
  };
});

const sectionStats = computed(() => {
  const statsMap = {};
  tableSections.value.forEach((section) => {
    statsMap[section.name] = {
      name: section.name,
      total: 0,
      available: 0,
      occupied: 0
    };
  });
  statsMap.__unassigned = { name: 'Sin sección', total: 0, available: 0, occupied: 0 };

  tables.value.forEach((table) => {
    const key = table.section || '__unassigned';
    if (!statsMap[key]) {
      statsMap[key] = {
        name: table.section || 'Sin sección',
        total: 0,
        available: 0,
        occupied: 0
      };
    }
    statsMap[key].total += 1;
    if (table.status === 'assigned') {
      statsMap[key].occupied += 1;
    } else {
      statsMap[key].available += 1;
    }
  });

  return Object.values(statsMap).filter((stat) => stat.total > 0);
});

const waiterLoad = computed(() => {
  return availableWaiters.value.map((waiter) => {
    const totalTables = tables.value.filter((table) => table.waiter === waiter.id).length;
    return {
      ...waiter,
      totalTables
    };
  });
});

const waiterStats = computed(() => {
  const map = {};
  availableWaiters.value.forEach((waiter) => {
    map[waiter.id] = {
      id: waiter.id,
      name: waiter.name,
      total: 0,
      occupied: 0
    };
  });
  map.__unassigned = { id: '__unassigned', name: 'Sin asignar', total: 0, occupied: 0 };

  tables.value.forEach((table) => {
    const key = table.waiter || '__unassigned';
    if (!map[key]) {
      map[key] = {
        id: key,
        name: 'Sin asignar',
        total: 0,
        occupied: 0
      };
    }
    map[key].total += 1;
    if (table.status === 'assigned') {
      map[key].occupied += 1;
    }
  });

  return Object.values(map).filter((stat) => stat.total > 0);
});

const tablesForReservation = (reservation) => (
  tables.value.filter((table) => {
    const sectionFilter = reservation?.selectedTableSection || 'all';
    if (!matchesSection(table.section, sectionFilter)) return false;
    return tableCanBeUsedForReservation(table, reservation);
  })
);

const getAvailableGroups = (reservation) => (
  tableGroups.value.filter((group) => {
    const sectionFilter = reservation?.selectedGroupSection || 'all';
    if (!matchesGroupSection(group, sectionFilter)) return false;
    return groupCanBeUsedForReservation(group, reservation);
  })
);

const groupSelectableTables = computed(() => {
  return tables.value.filter((table) => {
    if (table.status === 'assigned') return false;
    return matchesSection(table.section, groupSectionFilter.value);
  });
});

const quickGroupTablesForReservation = (reservation) => {
  if (!reservation) return [];
  const filter = reservation.quickGroupSection || 'all';
  return tables.value.filter((table) => {
    if (!tableCanBeUsedForReservation(table, reservation)) return false;
    return matchesSection(table.section, filter);
  });
};

const quickGroupCapacity = (reservation) => calculateQuickGroupCapacity(reservation, tables.value);

const isQuickGroupTableSelected = (reservation, tableId) => {
  const selection = reservation?.quickGroupTables || [];
  return selection.includes(tableId);
};

const hourlySummaryLabel = computed(() => {
  const today = getAdminISODate();
  const tomorrow = getAdminISODate(1);
  if (hourlySummaryDate.value === today) return 'hoy';
  if (hourlySummaryDate.value === tomorrow) return 'mañana';
  return formatAdminDate(hourlySummaryDate.value) || 'la fecha seleccionada';
});

const setHourlySummaryDateToToday = () => {
  hourlySummaryDate.value = getAdminISODate();
};

const setHourlySummaryDateToTomorrow = () => {
  hourlySummaryDate.value = getAdminISODate(1);
};

const activeWalkIns = computed(() => (
  walkIns.value
    .filter((walkIn) => walkIn.arrivalDate === (hourlySummaryDate.value || getAdminISODate()))
    .filter((walkIn) => !['cancelled', 'used'].includes(walkIn.status))
    .sort((a, b) => {
      const arrivalA = Date.parse(a.arrivalAt || '') || 0;
      const arrivalB = Date.parse(b.arrivalAt || '') || 0;
      return arrivalA - arrivalB;
    })
));

const selectedWalkIn = computed(() => (
  walkIns.value.find((walkIn) => walkIn.id === selectedWalkInId.value) || null
));

const queuedWalkIns = computed(() => activeWalkIns.value.filter((walkIn) => walkIn.status === 'queued'));

const attendedWalkIns = computed(() => (
  walkIns.value.filter((walkIn) => (
    walkIn.arrivalDate === (hourlySummaryDate.value || getAdminISODate())
    && walkIn.status === 'used'
  ))
));

const reservationHourlySummary = computed(() => {
  const targetDate = hourlySummaryDate.value || getAdminISODate();
  const summaryMap = new Map();

  reservations.value
    .filter((reservation) => (
      reservation.date === targetDate
      && isConfirmedReservation(reservation)
      && reservation.type !== 'walkin'
      && reservation.source !== 'admin_walkin'
      && reservation.time
    ))
    .forEach((reservation) => {
      const hourKey = String(reservation.time || '').slice(0, 2);
      if (!hourKey) {
        return;
      }

      if (!summaryMap.has(hourKey)) {
        summaryMap.set(hourKey, {
          hourKey,
          bucketTime: `${hourKey}:00`,
          totalPeople: 0
        });
      }

      const slot = summaryMap.get(hourKey);
      const people = Number(reservation.people || 0);
      slot.totalPeople += people;
    });

  return Array.from(summaryMap.values()).sort((a, b) => a.hourKey.localeCompare(b.hourKey));
});

const reservationHourlyTotals = computed(() => (
  reservationHourlySummary.value.reduce((totals, slot) => ({
    totalPeople: totals.totalPeople + slot.totalPeople
  }), {
    totalPeople: 0
  })
));

const walkInHourlySummary = computed(() => {
  const targetDate = hourlySummaryDate.value || getAdminISODate();
  const summaryMap = new Map();

  walkIns.value
    .filter((walkIn) => (
      walkIn.arrivalDate === targetDate
      && !['cancelled', 'used'].includes(walkIn.status)
      && walkIn.arrivalTime
    ))
    .forEach((walkIn) => {
      const hourKey = String(walkIn.arrivalTime || '').slice(0, 2);
      if (!hourKey) {
        return;
      }

      if (!summaryMap.has(hourKey)) {
        summaryMap.set(hourKey, {
          hourKey,
          bucketTime: `${hourKey}:00`,
          totalPeople: 0
        });
      }

      const slot = summaryMap.get(hourKey);
      slot.totalPeople += Number(walkIn.people || 0);
    });

  return Array.from(summaryMap.values()).sort((a, b) => a.hourKey.localeCompare(b.hourKey));
});

const walkInHourlyTotals = computed(() => (
  walkInHourlySummary.value.reduce((totals, slot) => ({
    totalPeople: totals.totalPeople + slot.totalPeople
  }), {
    totalPeople: 0
  })
));



// Iniciar escaneo de QR
    const stopQrScanner = () => {
      if (qrScannerInstance.value) {
        try {
          qrScannerInstance.value.stop();
          qrScannerInstance.value.destroy?.();
        } catch (err) {
          console.warn('No se pudo detener el escáner QR:', err);
        }
        qrScannerInstance.value = null;
      }
    };

    const startQRCodeScanner = async () => {
      stopQrScanner();
      scanning.value = true;

      await nextTick();

      try {
        const hasCamera = await QrScanner.hasCamera();
        if (!hasCamera) {
          alert('No se encontró una cámara en este dispositivo.');
          scanning.value = false;
          return;
        }

        if (!videoRef.value) {
          alert('No se pudo iniciar la cámara. Intenta nuevamente.');
          scanning.value = false;
          return;
        }

        qrScannerInstance.value = new QrScanner(
          videoRef.value,
          (result) => {
            scanning.value = false;
            stopQrScanner();
            handleScannedResult(result);
          },
          {
            highlightScanRegion: true,
            highlightCodeOutline: true,
            preferredCamera: 'environment'
          }
        );
        await qrScannerInstance.value.start();
      } catch (error) {
        console.error('Error al iniciar el escáner QR:', error);
        alert('No se pudo iniciar la cámara. Verifica permisos y vuelve a intentar.');
        scanning.value = false;
        stopQrScanner();
      }
    };

    // Manejar el resultado escaneado y buscar reservación por `id`
    const handleScannedResult = async (result) => {
      try {
        const scannedId = extractScannedId(result);
        if (!scannedId) {
          alert('No se pudo leer el código QR.');
          return;
        }
        const response = await scanReservationQrRequest(scannedId);
        const scannedReservation = response?.reservation;

        if (!scannedReservation) {
          alert('No se encontró la reservación.');
          return;
        }

        const waiterName = scannedReservation.tableWaiter
          ? getWaiterName(availableWaiters.value, scannedReservation.tableWaiter)
          : null;

        selectedReservation.value = {
          ...scannedReservation,
          waiterName
        };
        if (scannedReservation.recordType === 'walkin') {
          walkIns.value = walkIns.value.filter((walkIn) => walkIn.id !== scannedReservation.id);
        } else {
          removeReservationFromLists(scannedReservation.id);
        }
        showQRCodeModal.value = true;
      } catch (error) {
        console.error('Error al obtener los detalles de la reservación:', error);
        alert(error.message || 'Error al obtener los detalles de la reservación.');
      } finally {
        scanning.value = false;
      }
    };

    // Función para cerrar el modal de QR Code
    const closeQRCodeModal = () => {
      showQRCodeModal.value = false;
      selectedReservation.value = null; // Limpiar la reservación seleccionada al cerrar el modal
      stopQrScanner();
      scanning.value = false;
    };



    onMounted(() => {
      const today = getAdminISODate();

      const reservationsQuery = query(
        collection(db, 'reservations'),
        where('date', '>=', today)
      );

      const failedPaymentQuery = query(
        collection(db, 'reservations'),
        where('paymentStatus', '==', 'payment_failed')
      );

      const refundedQuery = query(
        collection(db, 'reservations'),
        where('paymentStatus', '==', 'refunded')
      );
      const paymentIncidentsQuery = query(
        collection(db, 'paymentIncidents'),
        where('status', '==', 'open')
      );
      onSnapshot(failedPaymentQuery, (snapshot) => {
        failedPaymentIncidentSource.value = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      });
      onSnapshot(refundedQuery, (snapshot) => {
        refundedIncidentSource.value = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      });
      onSnapshot(paymentIncidentsQuery, (snapshot) => {
        realtimePaymentIncidentSource.value = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      });

      onSnapshot(reservationsQuery, (snapshot) => {
        reservations.value = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            tableNumberInput: data.tableNumber || '',
            tableAssigned: Boolean(data.tableId || data.tableGroupId),
            selectedTableId: data.tableId || '',
            selectedGroupId: data.tableGroupId || '',
            selectedWaiterId: data.tableWaiter || '',
            selectedTableSection: 'all',
            selectedGroupSection: 'all',
            showQuickGroupPicker: false,
            quickGroupName: '',
            quickGroupSection: 'all',
            quickGroupTables: []
          };
        });

        filterReservations();
        updatePendingReservations();
      });

      onSnapshot(
        collection(db, 'walkIns'),
        (snapshot) => {
          walkIns.value = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data()
          })).sort((a, b) => {
            const orderA = Date.parse(a.arrivalAt || '') || 0;
            const orderB = Date.parse(b.arrivalAt || '') || 0;
            return orderA - orderB;
          });
        },
        (error) => {
          console.error('Error al cargar walk-ins:', error);
        }
      );

      onSnapshot(adminConfigRef, (configSnap) => {
        if (configSnap.exists()) {
          const data = configSnap.data();
          newReservationAlert.value = Boolean(data.newReservation);
          applyRemoteConfig(data);
          paymentEnabled.value = Boolean(data.paymentEnabled);
          paymentMode.value = ['deposit', 'full'].includes(data.paymentMode) ? data.paymentMode : 'deposit';
          paymentAmount.value = Number(data.paymentAmount) || 0;
          serviceFeeAmount.value = Number(data.serviceFeeAmount) || 0;
          paymentCurrency.value = String(data.paymentCurrency || 'USD').trim().toUpperCase() || 'USD';
          paymentOverrides.value = Array.isArray(data.paymentOverrides) ? data.paymentOverrides : [];
          autoConfirmAfterPayment.value = false;
          paymentProvider.value = String(data.paymentProvider || 'clover').trim() || 'clover';
        }
      });

      onSnapshot(collection(db, 'tables'), (snapshot) => {
        tables.value = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            status: data.status || 'available',
            capacity: data.capacity || 0,
            currentReservation: data.currentReservation || null
          };
        });
      });

      onSnapshot(collection(db, 'waiters'), (snapshot) => {
        availableWaiters.value = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
      });

      onSnapshot(collection(db, 'tableSections'), (snapshot) => {
        tableSections.value = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
      });

      onSnapshot(collection(db, 'tableGroups'), (snapshot) => {
        tableGroups.value = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            currentReservation: data.currentReservation || null,
            sections: data.sections || []
          };
        });
      });

      loadCloverWebhookStatus();
    });

    onUnmounted(() => {
      stopQrScanner();
    });


const filterReservations = () => {
    console.log('Filtrando reservaciones...');
    const searchLower = searchQuery.value.toLowerCase();

    filteredReservations.value = reservations.value.filter(reservation => {
      const emailMatches = reservation.email?.toLowerCase().includes(searchLower);
      const phoneMatches = normalizePhone(reservation.phone || '').includes(searchQuery.value.replace(/[^0-9]/g, ''));
      const isNotUsed = reservation.status !== 'used';
      const isLegacyWalkIn = reservation.type === 'walkin' || reservation.source === 'admin_walkin';
      const isValidStatus = !INVALID_STATUSES.includes(reservation.status) && reservation.status !== 'awaiting_payment';
      const isValidPayment = !INVALID_PAYMENT_STATUSES.includes(reservation.paymentStatus);

      return (emailMatches || phoneMatches) && isNotUsed && !isLegacyWalkIn && isValidStatus && isValidPayment;
    }).sort((a, b) => {
      const dateA = toAdminDateTime(a.date, a.time);
      const dateB = toAdminDateTime(b.date, b.time);
      return dateA - dateB;
    });

    console.log('Reservaciones filtradas:', filteredReservations.value);
  };




    const formatTime = (time) => formatTimeLabel(time);

    const updatePendingReservations = () => {
      pendingReservations.value = reservations.value.filter((reservation) => (
        reservation.status === 'pending'
        && reservation.type !== 'walkin'
        && reservation.source !== 'admin_walkin'
        && reservation.paymentStatus !== 'awaiting_payment'
      ));
    };

    const getStatusClass = (status) => {
      if (status === 'confirmed') return 'confirmed';
      if (status === 'denied') return 'denied';
      if (status === 'cancelled') return 'cancelled';
      return 'pending';
    };

// Función para confirmar la reservación y enviar el correo con QR
const confirmReservation = async (reservation) => {
  loading.value = true;

  try {
    const response = await confirmReservationRequest(reservation.id);
    reservation.status = 'confirmed';
    if (response?.confirmedAt) {
      reservation.confirmedAt = response.confirmedAt;
    }
    alert(response.message || 'Reservación confirmada y correo enviado.');
  } catch (error) {
    console.error('Error al confirmar la reservación:', error);
    alert(`Error al confirmar la reservación: ${error.message}`);
  } finally {
    loading.value = false;
  }
};

const resendConfirmationEmail = async (reservation) => {
  loading.value = true;
  try {
    if (reservation.status !== 'confirmed') {
      const response = await confirmReservationRequest(reservation.id);
      reservation.status = 'confirmed';
      if (response?.confirmedAt) {
        reservation.confirmedAt = response.confirmedAt;
      }
      alert(response.message || 'Correo de confirmación reenviado.');
      return;
    }

    const response = await resendReservationConfirmationRequest(reservation.id);
    alert(response.message || 'Correo de confirmación reenviado.');
  } catch (error) {
    console.error('Error al reenviar la confirmación:', error);
    alert(`Error al reenviar la confirmación: ${error.message}`);
  } finally {
    loading.value = false;
  }
};

// Función para denegar la reservación
const denyReservation = async (reservation) => {
  loading.value = true;

  try {
    const response = await denyReservationRequest(reservation.id);
    reservation.status = 'denied';
    if (response?.deniedAt) {
      reservation.deniedAt = response.deniedAt;
    }
    alert(response.message || 'Reservación denegada con éxito.');
  } catch (error) {
    console.error('Error al denegar la reservación:', error);
    alert(`Error al denegar la reservación: ${error.message}`);
  } finally {
    loading.value = false;
  }
};


    const retryFailedPaymentEmail = async (reservation) => {
      loading.value = true;
      try {
        await retryPaymentIncidentNotificationRequest(reservation.id);
        reservation.paymentFailedEmailStatus = 'sent';
        reservation.paymentFailedEmailSentAt = new Date().toISOString();
        alert(`Email reenviado a ${reservation.email}`);
      } catch (error) {
        console.error('Error al reenviar notificación de pago fallido:', error);
        alert(error.message || 'No se pudo reenviar el email.');
      } finally {
        loading.value = false;
      }
    };

    const retryPaymentIncidentEmail = async (incident) => {
      if (!incident?.canRetryEmail) {
        return;
      }
      await retryFailedPaymentEmail(incident.reservation);
    };

    const cancelFailedPaymentReservation = async (reservation) => {
      if (!confirm(`¿Cancelar la reservación de ${reservation.name}? Se notificará al cliente.`)) return;
      loading.value = true;
      try {
        const reason = 'Pago no completado';
        await cancelReservationRequest(reservation.id, reason);
        reservation.status = 'cancelled';
        reservation.cancellationReason = reason;
      } catch (error) {
        console.error('Error al cancelar reservación fallida:', error);
        alert(error.message || 'No se pudo cancelar la reservación.');
      } finally {
        loading.value = false;
      }
    };

    const cancelReservation = async (reservation) => {
      loading.value = true;
      try {
        const reason = prompt('Por favor, ingrese la razón de la cancelación:');
        if (!reason) {
          alert('Debe proporcionar una razón para la cancelación.');
          return;
        }

        const response = await cancelReservationRequest(reservation.id, reason);
        reservation.status = 'cancelled';
        reservation.cancellationReason = reason;
        reservation.tableId = null;
        reservation.tableNumber = null;
        reservation.tableSection = null;
        reservation.tableWaiter = null;
        reservation.tableGroupId = null;
        reservation.tableGroupName = null;
        reservation.selectedTableId = '';
        reservation.selectedGroupId = '';
        reservation.selectedWaiterId = '';
        alert(response.message || 'Reservación cancelada con éxito');
      } catch (error) {
        console.error('Error al cancelar la reservación:', error);
        alert(error.message || 'Error al cancelar la reservación y enviar la notificación');
      } finally {
        loading.value = false;
      }
    };

    const addManualTable = async (reservation) => {
      const currentTable = reservation.tableNumber ? String(reservation.tableNumber) : '';
      const input = prompt('Número de mesa', currentTable);
      if (input === null) return;
      const value = input.trim();
      if (!value) {
        alert('Debes escribir un número de mesa.');
        return;
      }

      loading.value = true;
      try {
        const response = await setReservationManualTableRequest(reservation.id, value);
        reservation.tableNumber = value;
        reservation.tableGroupId = null;
        reservation.tableGroupName = null;
        reservation.tableId = null;
        reservation.tableSection = null;
        reservation.tableWaiter = null;
        reservation.tableNumberInput = value;
        alert(response.message || 'Mesa asignada manualmente.');
      } catch (error) {
        console.error('Error al asignar mesa manual:', error);
        alert('No se pudo asignar la mesa manualmente.');
      } finally {
        loading.value = false;
      }
    };



    const deleteReservation = async (reservation) => {
      loading.value = true;
      try {
        const response = await deleteReservationRequest(reservation.id);
        removeReservationFromLists(reservation.id);
        alert(response.message || 'Reservación eliminada con éxito');
      } catch (error) {
        console.error('Error al eliminar la reservación:', error);
        alert('No se pudo eliminar la reservación.');
      } finally {
        loading.value = false;
      }
    };

    const markDepositApplied = async (reservation) => {
      loading.value = true;
      try {
        await markDepositAppliedRequest(reservation.id);
        reservation.depositApplied = true;
        reservation.depositAppliedAt = new Date().toISOString();
      } catch (error) {
        alert(error.message || 'No se pudo marcar el depósito como aplicado.');
      } finally {
        loading.value = false;
      }
    };

    const cleanOldReservations = async () => {
      cleaningOldReservations.value = true;
      cleanedReservationsCount.value = 0;
      try {
        const response = await cleanOldReservationsRequest();
        const deleted = Number(response?.deleted || 0);
        cleanedReservationsCount.value = deleted;
        alert(`Se limpiaron ${deleted} reservaciones con más de 24 horas.`);
      } catch (error) {
        console.error('Error al limpiar reservaciones pasadas:', error);
        alert('No se pudieron limpiar las reservaciones pasadas.');
      } finally {
        cleaningOldReservations.value = false;
      }
    };

    const backfillHistoricalVoids = async () => {
      if (!confirm('Esto buscará auto-voids históricos de Clover y los convertirá en incidentes visibles en el panel. No enviará emails. ¿Continuar?')) {
        return;
      }

      backfillingHistoricalVoids.value = true;
      try {
        const response = await backfillHistoricalVoidIncidentsRequest({
          lookbackDays: 120,
          limit: 200,
          dryRun: false
        });
        alert(
          `Backfill completado.\n`
          + `Reservaciones revisadas: ${Number(response?.scanned || 0)}\n`
          + `Órdenes Clover encontradas: ${Number(response?.matchedOrders || 0)}\n`
          + `Voids detectados: ${Number(response?.detectedVoids || 0)}\n`
          + `Incidentes creados: ${Number(response?.created || 0)}\n`
          + `Incidentes actualizados: ${Number(response?.updated || 0)}\n`
          + `Saltadas: ${Number(response?.skipped || 0)}`
        );
      } catch (error) {
        console.error('Error ejecutando backfill histórico de auto-voids:', error);
        alert(error.message || 'No se pudo ejecutar el backfill de auto-voids.');
      } finally {
        backfillingHistoricalVoids.value = false;
      }
    };


    const purgePendingReservations = async () => {
      loading.value = true;
      try {
        const result = await purgePendingReservationsRequest();
        if (result?.deleted) {
          alert(`Se eliminaron ${result.deleted} reservaciones pendientes.`);
        } else {
          alert('No se eliminaron reservaciones pendientes.');
        }
      } catch (error) {
        console.error('Error al eliminar reservaciones pendientes:', error);
        alert('No se pudieron eliminar las reservaciones pendientes.');
      } finally {
        loading.value = false;
      }
    };

    const purgePendingReservationsByNames = async (names = []) => {
      loading.value = true;
      try {
        const result = await purgePendingReservationsRequest(names);
        if (result?.deleted) {
          alert(`Se eliminaron ${result.deleted} reservaciones pendientes.`);
        } else {
          alert('No se eliminaron reservaciones pendientes.');
        }
      } catch (error) {
        console.error('Error al eliminar reservaciones pendientes por nombre:', error);
        alert('No se pudieron eliminar las reservaciones pendientes.');
      } finally {
        loading.value = false;
      }
    };

    const resetNewTableForm = () => {
      newTableForm.value = {
        section: '',
        number: '',
        capacity: 4,
        waiter: ''
      };
    };

    const addTable = async () => {
      if (!newTableForm.value.section || !newTableForm.value.number) {
        alert('Completa la sección y el número de la mesa.');
        return;
      }

      try {
        const response = await createTableRequest({
          section: newTableForm.value.section,
          number: newTableForm.value.number,
          capacity: Number(newTableForm.value.capacity) || 0,
          waiter: newTableForm.value.waiter || null
        });

        resetNewTableForm();
        alert(response.message || 'Mesa creada con éxito.');
      } catch (error) {
        console.error('Error al crear la mesa:', error);
        alert('No se pudo crear la mesa.');
      }
    };

    const startEditTable = (table) => {
      editingTableId.value = table.id;
      editTableData.value = {
        section: table.section || '',
        number: table.number || '',
        capacity: table.capacity || 0,
        waiter: table.waiter || ''
      };
    };

    const cancelEditTable = () => {
      editingTableId.value = null;
    };

    const saveTableEdits = async () => {
      if (!editingTableId.value) return;
      try {
        const response = await updateTableRequest({
          tableId: editingTableId.value,
          section: editTableData.value.section,
          number: Number(editTableData.value.number),
          capacity: Number(editTableData.value.capacity),
          waiter: editTableData.value.waiter || null
        });
        editingTableId.value = null;
        alert(response.message || 'Mesa actualizada.');
      } catch (error) {
        console.error('Error al actualizar la mesa:', error);
        alert('No se pudo actualizar la mesa.');
      }
    };

    const deleteTable = async (tableId) => {
      const table = tables.value.find((item) => item.id === tableId);
      if (table?.status === 'assigned') {
        alert('Libera la mesa antes de eliminarla.');
        return;
      }

      const confirmDelete = window.confirm('¿Eliminar esta mesa?');
      if (!confirmDelete) return;

      try {
        const response = await deleteTableRequest(tableId);
        if (editingTableId.value === tableId) {
          editingTableId.value = null;
        }
        alert(response.message || 'Mesa eliminada.');
      } catch (error) {
        console.error('Error al eliminar la mesa:', error);
        alert('No se pudo eliminar la mesa.');
      }
    };

    const addWaiter = async () => {
      const name = newWaiterName.value.trim();
      if (!name) {
        alert('Escribe un nombre para el mesero.');
        return;
      }

      try {
        const response = await createWaiterRequest(name);
        newWaiterName.value = '';
        alert(response.message || 'Mesero creado.');
      } catch (error) {
        console.error('Error al crear mesero:', error);
        alert('No se pudo crear el mesero.');
      }
    };

    const deleteWaiter = async (waiterId) => {
      const hasTables = tables.value.some((table) => table.waiter === waiterId);
      if (hasTables) {
        alert('Reasigna las mesas antes de eliminar al mesero.');
        return;
      }

      const confirmDelete = window.confirm('¿Eliminar este mesero?');
      if (!confirmDelete) return;

      try {
        const response = await deleteWaiterRequest(waiterId);
        alert(response.message || 'Mesero eliminado.');
      } catch (error) {
        console.error('Error al eliminar mesero:', error);
        alert('No se pudo eliminar el mesero.');
      }
    };

    const addSection = async () => {
      const name = newSectionName.value.trim();
      if (!name) {
        alert('Escribe un nombre para la sección.');
        return;
      }

      try {
        const response = await createSectionRequest(name);
        newSectionName.value = '';
        alert(response.message || 'Sección creada.');
      } catch (error) {
        console.error('Error al crear sección:', error);
        alert('No se pudo crear la sección.');
      }
    };

    const deleteSection = async (section) => {
      if (!section?.id) return;
      const hasTables = tables.value.some((table) => table.section === section.name);
      if (hasTables) {
        alert('Reubica las mesas antes de eliminar la sección.');
        return;
      }

      const confirmDelete = window.confirm('¿Eliminar esta sección?');
      if (!confirmDelete) return;
      try {
        const response = await deleteSectionRequest(section.id);
        alert(response.message || 'Sección eliminada.');
      } catch (error) {
        console.error('Error al eliminar la sección:', error);
        alert('No se pudo eliminar la sección.');
      }
    };

const toggleGroupTable = (tableId) => {
  if (newGroupTables.value.includes(tableId)) {
    newGroupTables.value = newGroupTables.value.filter((id) => id !== tableId);
  } else {
    newGroupTables.value = [...newGroupTables.value, tableId];
  }
};

const createTableGroup = async () => {
  const name = newGroupName.value.trim();
  if (!name || !newGroupTables.value.length) {
    alert('Selecciona un nombre y al menos una mesa.');
    return;
      }

      const selectedTables = tables.value.filter((table) => newGroupTables.value.includes(table.id));
      if (!selectedTables.length) {
        alert('No se encontraron las mesas seleccionadas.');
        return;
      }

      const unavailable = selectedTables.filter((table) => table.status === 'assigned');
      if (unavailable.length) {
        alert('Todas las mesas deben estar disponibles para crear un grupo.');
        return;
      }

      try {
        const response = await createTableGroupRequest({
          name,
          tableIds: selectedTables.map((table) => table.id)
        });
        newGroupName.value = '';
        newGroupTables.value = [];
        alert(response.message || 'Grupo creado.');
      } catch (error) {
        console.error('Error al crear el grupo:', error);
        alert('No se pudo crear el grupo.');
      }
    };

    const releaseGroup = async (group) => {
      if (!group?.id) return;
      try {
        const response = await releaseTableGroupRequest(group.id);
        alert(response.message || 'Grupo liberado.');
      } catch (error) {
        console.error('Error al liberar el grupo:', error);
        alert('No se pudo liberar el grupo.');
      }
    };

    const deleteTableGroup = async (group) => {
      if (group.status === 'assigned') {
        alert('Libera el grupo antes de eliminarlo.');
        return;
      }

      const confirmDelete = window.confirm('¿Eliminar este grupo de mesas?');
      if (!confirmDelete) return;

      try {
        const response = await deleteTableGroupRequest(group.id);
        alert(response.message || 'Grupo eliminado.');
      } catch (error) {
        console.error('Error al eliminar el grupo:', error);
        alert('No se pudo eliminar el grupo.');
      }
    };

    const openFailedPaymentsModal = () => { showFailedPaymentsModal.value = true; };
    const closeFailedPaymentsModal = () => { showFailedPaymentsModal.value = false; };
    const openPaymentIncidentsModal = () => { showPaymentIncidentsModal.value = true; };
    const closePaymentIncidentsModal = () => { showPaymentIncidentsModal.value = false; };

    const openWalkInModal = () => {
      showWalkInModal.value = true;
    };

    const toggleWalkInDetails = (walkInId) => {
      selectedWalkInId.value = selectedWalkInId.value === walkInId ? '' : walkInId;
    };

    const closeWalkInDetails = () => {
      selectedWalkInId.value = '';
    };

    const handleWalkInSaved = (response) => {
      alert(response?.message || 'Walk-in registrado correctamente.');
      closeWalkInModal();
      selectedWalkInId.value = '';
    };

    const closeWalkInModal = () => {
      showWalkInModal.value = false;
    };

    const seatWalkIn = async (walkIn) => {
      const confirmed = window.confirm(`Marcar a ${walkIn.name} como atendido?`);
      if (!confirmed) {
        return;
      }

      loading.value = true;
      try {
        const response = await seatWalkInRequest(walkIn.id);
        walkIn.status = 'used';
        walkIn.tableNumber = response?.tableNumber || walkIn.tableNumber || null;
        walkIn.usedAt = response?.usedAt || walkIn.usedAt;
        if (selectedWalkInId.value === walkIn.id) {
          selectedWalkInId.value = '';
        }
        walkIns.value = walkIns.value.filter((item) => item.id !== walkIn.id);
        alert(response.message || 'Walk-in marcado como atendido.');
      } catch (error) {
        console.error('Error al marcar el walk-in como atendido:', error);
        alert(error.message || 'No se pudo marcar el walk-in como atendido.');
      } finally {
        loading.value = false;
      }
    };

    const cancelWalkIn = async (walkIn) => {
      const reason = prompt('Razón para cancelar el walk-in (opcional):', '');
      if (reason === null) return;

      loading.value = true;
      try {
        const response = await cancelWalkInRequest(walkIn.id, reason);
        walkIn.status = 'cancelled';
        walkIn.cancellationReason = reason;
        if (selectedWalkInId.value === walkIn.id) {
          closeWalkInDetails();
        }
        alert(response.message || 'Walk-in cancelado.');
      } catch (error) {
        console.error('Error al cancelar el walk-in:', error);
        alert(error.message || 'No se pudo cancelar el walk-in.');
      } finally {
        loading.value = false;
      }
    };

const quickGroupTables = (reservation) => quickGroupTablesForReservation(reservation);

const defaultGroupNameForReservation = (reservation) => {
  const base = reservation?.name?.split(' ')[0] || reservation?.id?.slice(-4) || 'evento';
  return `Grupo ${base}`;
};

const toggleQuickGroupPicker = (reservation) => {
  if (!reservation) return;
  reservation.showQuickGroupPicker = !reservation.showQuickGroupPicker;
};

const toggleQuickGroupTableSelection = (reservation, tableId) => {
  if (!reservation) return;
  if (!reservation.quickGroupTables) {
    reservation.quickGroupTables = [];
  }
  if (reservation.quickGroupTables.includes(tableId)) {
    reservation.quickGroupTables = reservation.quickGroupTables.filter((id) => id !== tableId);
  } else {
    reservation.quickGroupTables = [...reservation.quickGroupTables, tableId];
  }
};

const resetQuickGroupBuilder = (reservation) => {
  if (!reservation) return;
  reservation.quickGroupName = '';
  reservation.quickGroupTables = [];
  reservation.quickGroupSection = 'all';
  reservation.showQuickGroupPicker = false;
};

const createQuickGroupFromCard = async (reservation) => {
  if (!reservation) return;
  let name = (reservation.quickGroupName || '').trim();
  if (!name) {
    name = defaultGroupNameForReservation(reservation);
  }
  if (!reservation.quickGroupTables.length) {
    alert('Selecciona al menos una mesa para el grupo.');
    return;
  }

  const selectedTables = tables.value.filter((table) => reservation.quickGroupTables.includes(table.id));
  if (!selectedTables.length) {
    alert('No se encontraron las mesas seleccionadas.');
    return;
  }

  const unavailable = selectedTables.filter((table) => table.status === 'assigned' && table.currentReservation?.reservationId !== reservation.id);
  if (unavailable.length) {
    alert('Todas las mesas deben estar disponibles para crear este grupo.');
    return;
  }

  loading.value = true;
  try {
    const response = await createTableGroupRequest({
      name,
      tableIds: selectedTables.map((table) => table.id),
      reservationId: reservation.id
    });

    reservation.selectedGroupId = response.groupId;
    reservation.quickGroupName = '';
    reservation.quickGroupTables = [];
    reservation.quickGroupSection = 'all';
    reservation.showQuickGroupPicker = false;
    await assignTableGroup(reservation, response.groupId);
  } catch (error) {
    console.error('Error al crear el grupo rápido:', error);
    alert('No se pudo crear el grupo desde la tarjeta.');
  } finally {
    loading.value = false;
  }
};

const selectReservationForAction = (reservationId) => {
  const element = document.getElementById(`reservation-${reservationId}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

    const openEditModal = (reservation) => {
      selectedReservation.value = { ...reservation };
      showEditModal.value = true;
    };

    const closeEditModal = () => {
      showEditModal.value = false;
    };

    const openReservationModal = () => {
      showReservationModal.value = true;
    };

    const closeReservationModal = () => {
      showReservationModal.value = false;
    };

    const openScannedReservationsModal = () => {
      showScannedReservationsModal.value = true;
    };

    const closeScannedReservationsModal = () => {
      showScannedReservationsModal.value = false;
    };

    const assignTable = async (reservation) => {
      if (!reservation.selectedTableId) {
        alert('Selecciona una mesa disponible.');
        return;
      }

      loading.value = true;
      try {
        const response = await assignReservationTableRequest(reservation.id, reservation.selectedTableId);
        const assignment = response?.assignment || {};
        reservation.tableNumber = assignment.tableNumber ?? null;
        reservation.tableId = assignment.tableId ?? null;
        reservation.tableSection = assignment.tableSection ?? null;
        reservation.tableWaiter = assignment.tableWaiter ?? null;
        reservation.tableNumberInput = assignment.tableNumber ?? '';
        reservation.tableAssigned = true;
        reservation.tableGroupId = null;
        reservation.tableGroupName = null;
        reservation.selectedGroupId = '';
        reservation.selectedWaiterId = assignment.tableWaiter || '';
        reservation.seatedAt = assignment.seatedAt || reservation.seatedAt;
        alert(response.message || `Mesa asignada a ${reservation.name}`);
      } catch (error) {
        console.error('Error al asignar la mesa:', error);
        alert(error.message || 'Error al asignar la mesa.');
      } finally {
        loading.value = false;
      }
    };

    const releaseReservationTable = async (reservation) => {
      if (!reservation.tableId) return;
      loading.value = true;
      try {
        const response = await releaseReservationTableRequest(reservation.id);
        reservation.tableId = null;
        reservation.tableNumber = null;
        reservation.tableSection = null;
        reservation.tableWaiter = null;
        reservation.tableNumberInput = '';
        reservation.selectedTableId = '';
        reservation.tableAssigned = false;
        reservation.selectedWaiterId = '';
        alert(response.message || 'Mesa liberada para esta reservación.');
      } catch (error) {
        console.error('Error al liberar la mesa:', error);
        alert('No se pudo liberar la mesa.');
      } finally {
        loading.value = false;
      }
    };

    const assignTableGroup = async (reservation, overrideGroupId = null) => {
      const groupIdToUse = overrideGroupId || reservation.selectedGroupId;
      if (!groupIdToUse) {
        alert('Selecciona un grupo disponible.');
        return;
      }
      loading.value = true;
      try {
        const response = await assignReservationGroupRequest(reservation.id, groupIdToUse);
        const assignment = response?.assignment || {};
        reservation.tableGroupId = assignment.groupId ?? null;
        reservation.tableGroupName = assignment.groupName ?? null;
        reservation.tableId = null;
        reservation.tableNumber = null;
        reservation.tableSection = null;
        reservation.tableWaiter = assignment.tableWaiter ?? null;
        reservation.tableNumberInput = '';
        reservation.selectedTableId = '';
        reservation.tableAssigned = true;
        reservation.selectedWaiterId = assignment.tableWaiter || '';
        reservation.seatedAt = assignment.seatedAt || reservation.seatedAt;

        alert(response.message || `Grupo asignado a ${reservation.name}`);
      } catch (error) {
        console.error('Error al asignar el grupo:', error);
        alert(error.message || 'No se pudo asignar el grupo.');
      } finally {
        loading.value = false;
      }
    };

    const releaseReservationGroup = async (reservation) => {
      if (!reservation.tableGroupId) return;
      loading.value = true;
      try {
        const response = await releaseReservationGroupRequest(reservation.id);
        reservation.tableGroupId = null;
        reservation.tableGroupName = null;
        reservation.tableWaiter = null;
        reservation.selectedGroupId = '';
        reservation.selectedWaiterId = '';
        reservation.tableAssigned = false;
        alert(response.message || 'Grupo liberado para esta reservación.');
      } catch (error) {
        console.error('Error al liberar el grupo:', error);
        alert('No se pudo liberar el grupo.');
      } finally {
        loading.value = false;
      }
    };

    const assignWaiterToReservation = async (reservation) => {
      if (!reservation.selectedWaiterId) {
        alert('Selecciona un mesero.');
        return;
      }

      loading.value = true;
      try {
        const response = await assignReservationWaiterRequest(reservation.id, reservation.selectedWaiterId);
        reservation.tableWaiter = reservation.selectedWaiterId;
        alert(response.message || 'Mesero asignado.');
      } catch (error) {
        console.error('Error al asignar mesero:', error);
        alert('No se pudo asignar el mesero.');
      } finally {
        loading.value = false;
      }
    };

const updateReservation = async (reservation) => {
  loading.value = true;
  try {
    const response = await updateReservationRequest({
      id: reservation.id,
      name: reservation.name,
      email: reservation.email,
      phone: reservation.phone,
      date: reservation.date,
      time: reservation.time,
      people: reservation.people,
      tableNumber: reservation.tableNumber,
      specialNotes: reservation.specialNotes
    });
    alert(response.message || 'Reservación actualizada con éxito');
    closeEditModal();
  } catch (error) {
    console.error('Error al actualizar la reservación:', error);
    alert(error.message || 'No se pudo actualizar la reservación.');
  } finally {
    loading.value = false;
  }
};

    const goToNewReservation = () => {
      newReservationAlert.value = false;
      if (pendingReservations.value.length) {
        selectReservationForAction(pendingReservations.value[0].id);
      }
    };

    const openTablesModal = () => {
      showTablesModal.value = true;
    };

    const closeTablesModal = () => {
      showTablesModal.value = false;
    };

    const removeReservationFromLists = (reservationId) => {
      reservations.value = reservations.value.filter(res => res.id !== reservationId);
      filterReservations();
      updatePendingReservations();
    };

    if (typeof window !== 'undefined') {
      window.__adminPurgePending = purgePendingReservations;
      window.__adminPurgePendingByNames = purgePendingReservationsByNames;
    }

    return {
      searchQuery,
      reservations,
      walkIns,
      filteredReservations,
      pendingReservations,
      newReservationAlert,
      cleanedReservationsCount,
      cleaningOldReservations,
      backfillingHistoricalVoids,
      paymentEnabled,
      paymentMode,
      paymentAmount,
      serviceFeeAmount,
      paymentCurrency,
      autoConfirmAfterPayment,
      paymentProvider,
      formattedPaymentAmount,
      formatReservationPaymentAmount,
      formatCurrencyAmount,
      formatIncidentTimestamp,
      paymentSettingsConfig,
      paymentSettingsSummary,
      cloverWebhookStatus,
      loadingCloverWebhookStatus,
      showPaymentSettingsModal,
      selectedReservation,
      showEditModal,
      showReservationModal,
      showScannedReservationsModal,
      loading,
      scanning,
      videoRef,
      showQRCodeModal,
      scannedReservationId,
      selectedWalkInId,
      selectedWalkIn,
      disabledTimes,
      selectedDate,
      allPossibleTimes,
      showScheduleModal,
      showTablesModal,
      showWalkInModal,
      showFailedPaymentsModal,
      openFailedPaymentsModal,
      closeFailedPaymentsModal,
      showPaymentIncidentsModal,
      openPaymentIncidentsModal,
      closePaymentIncidentsModal,
      tablesModalTab,
      tableTabOptions,
      scheduleTab,
      selectedWeeklyDay,
      weeklyTemplates,
      weeklyDayLabels,
      weeklyDayOptions,
      customWeeklyTime,
      getDayLabel,
      newDayLabel,
      showCalendarModal,
      tables,
      tableGroups,
      tableSections,
      availableWaiters,
      filteredTables,
      tableStats,
      sectionStats,
      waiterStats,
      waiterLoad,
      activeWalkIns,
      queuedWalkIns,
      attendedWalkIns,
      reservationHourlySummary,
      reservationHourlyTotals,
      walkInHourlySummary,
      walkInHourlyTotals,
      hourlySummaryDate,
      hourlySummaryLabel,
      formatAdminDate,
      selectedTableSection,
      selectedTableStatus,
      tableSearch,
      newTableForm,
      editingTableId,
      editTableData,
      newWaiterName,
      newSectionName,
      newGroupName,
      newGroupTables,
      groupSectionFilter,
      groupSelectableTables,
      quickGroupTables,
      quickGroupCapacity,
      isQuickGroupTableSelected,
      editingWeeklyTime,
      editingWeeklyTimeValue,
      editingDayKey,
      editingDayLabel,
      selectWeeklyDay,
      addWeeklyDay,
      startEditDay,
      saveDayLabel,
      cancelEditDay,
      deleteWeeklyDay,
      startEditWeeklyTime,
      saveWeeklyTimeEdit,
      cancelEditWeeklyTime,
      removeWeeklyTime,
      specialNotes,
      formatTimeTo12Hour,
      formatTime,
      isTimeDisabled,
      WEEKLY_DAY_LABELS,
      startQRCodeScanner,
      filterReservations,
      updatePendingReservations,
      getStatusClass,
      confirmReservation,
      denyReservation,
      resendConfirmationEmail,
      cancelReservation,
      addManualTable,
      deleteReservation,
      markDepositApplied,
      purgePendingReservations,
      selectReservationForAction,
      openEditModal,
      closeEditModal,
      openReservationModal,
      closeReservationModal,
      openScannedReservationsModal,
      closeScannedReservationsModal,
      assignTable,
      releaseReservationTable,
      openPaymentSettingsModal,
      closePaymentSettingsModal,
      loadCloverWebhookStatus,
      handlePaymentSettingsSaved,
      openScheduleModal,
      closeScheduleModal,
      openCalendarModal,
      closeCalendarModal,
      goToNewReservation,
      openTablesModal,
      closeTablesModal,
      openWalkInModal,
      toggleWalkInDetails,
      closeWalkInDetails,
      handleWalkInSaved,
      closeWalkInModal,
      seatWalkIn,
      cancelWalkIn,
      setHourlySummaryDateToToday,
      setHourlySummaryDateToTomorrow,
      jumpToNextWeekend,
      toggleTime,
      addCustomWeeklyTime,
      loadTimesForDate,
      updateReservation,
      closeQRCodeModal,
      addTable,
      startEditTable,
      cancelEditTable,
      saveTableEdits,
      deleteTable,
      addWaiter,
      deleteWaiter,
      addSection,
      deleteSection,
      toggleGroupTable,
      createTableGroup,
      deleteTableGroup,
      releaseGroup,
      assignTableGroup,
      releaseReservationGroup,
      assignWaiterToReservation,
      toggleQuickGroupTableSelection,
      toggleQuickGroupPicker,
      resetQuickGroupBuilder,
      createQuickGroupFromCard,
      cleanOldReservations,
      backfillHistoricalVoids,
      tablesForReservation,
      getAvailableGroups,
      getWaiterName,
      logo: logoImage,
      confirmedReservations,
      paymentIncidents,
      paymentIncidentAlert,
      failedPaymentReservations,
      failedPaymentAlert,
      refundedReservations,
      refundedAlert,
      retryPaymentIncidentEmail,
      retryFailedPaymentEmail,
      cancelFailedPaymentReservation
    };

  }
};
</script>
<style src="./AdminPanel.css"></style>
